import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAgentAssignments, deleteAgentAssignment } from '../../actions/agentAssignment';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { getAgents } from '../../actions/agent';
import { Plus, Search, Edit, Trash2, Users, Eye, AlertTriangle, Activity, UserCheck, Map, Globe } from 'lucide-react';

const AgentSectorAssignments = ({ 
    getAgentAssignments, 
    deleteAgentAssignment, 
    getServiceableAreas,
    getAgents,
    agentAssignments, 
    serviceableAreas,
    agents,
    loading 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [assignmentToDelete, setAssignmentToDelete] = useState(null);
    const [filteredAssignments, setFilteredAssignments] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [serviceableAreaFilter, setServiceableAreaFilter] = useState('all');

    useEffect(() => {
        document.title = 'Agent Assignments - Management';
        getAgentAssignments();
        getServiceableAreas();
        getAgents();
    }, [getAgentAssignments, getServiceableAreas, getAgents]);

    useEffect(() => {
        if (agentAssignments) {
            let filtered = agentAssignments.filter(assignment =>
                assignment.agent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                assignment.serviceableArea?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                assignment.sectors?.some(sector => 
                    sector.name?.toLowerCase().includes(searchTerm.toLowerCase())
                ) ||
                assignment.notes?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (statusFilter !== 'all') {
                filtered = filtered.filter(assignment =>
                    statusFilter === 'active' ? assignment.isActive : !assignment.isActive
                );
            }

            if (typeFilter !== 'all') {
                filtered = filtered.filter(assignment =>
                    typeFilter === 'floating' ? assignment.isFloating : !assignment.isFloating
                );
            }

            if (serviceableAreaFilter !== 'all') {
                filtered = filtered.filter(assignment =>
                    assignment.serviceableArea?._id === serviceableAreaFilter
                );
            }

            setFilteredAssignments(filtered);
        }
    }, [agentAssignments, searchTerm, statusFilter, typeFilter, serviceableAreaFilter]);

    const handleDeleteClick = (assignment) => {
        setAssignmentToDelete(assignment);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (assignmentToDelete) {
            deleteAgentAssignment(assignmentToDelete._id);
            setShowDeleteModal(false);
            setAssignmentToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setAssignmentToDelete(null);
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'secondary';
    };

    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };

    const getTypeColor = (isFloating) => {
        return isFloating ? 'info' : 'warning';
    };

    const getTypeText = (isFloating) => {
        return isFloating ? 'Floating' : 'Fixed';
    };

    if (loading && !agentAssignments) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading agent assignments...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary mb-1 fw-bold">
                                    <Users className="me-2" size={32} />
                                    Agent Sector Assignments
                                </h2>
                                <p className="text-muted mb-0">Manage agent assignments to serviceable areas and sectors</p>
                            </div>
                            <Link
                                to="/inventory/v1.2/agent-sector-assignments/new"
                                className="btn btn-primary btn-lg d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Create Assignment
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <Search size={18} />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by agent, area, sector, or notes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="floating">Floating Only</option>
                            <option value="fixed">Fixed Only</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={serviceableAreaFilter}
                            onChange={(e) => setServiceableAreaFilter(e.target.value)}
                        >
                            <option value="all">All Serviceable Areas</option>
                            {serviceableAreas?.map(area => (
                                <option key={area._id} value={area._id}>
                                    {area.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Total Assignments</h5>
                                        <h3 className="mt-2 mb-0">{agentAssignments?.length || 0}</h3>
                                    </div>
                                    <Users size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Active Assignments</h5>
                                        <h3 className="mt-2 mb-0">
                                            {agentAssignments?.filter(assignment => assignment.isActive).length || 0}
                                        </h3>
                                    </div>
                                    <Activity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Floating Agents</h5>
                                        <h3 className="mt-2 mb-0">
                                            {agentAssignments?.filter(assignment => assignment.isFloating).length || 0}
                                        </h3>
                                    </div>
                                    <Globe size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Filtered Results</h5>
                                        <h3 className="mt-2 mb-0">{filteredAssignments?.length || 0}</h3>
                                    </div>
                                    <Search size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Agent Assignments List</h5>
                    </div>
                    <div className="card-body p-0">
                        {filteredAssignments && filteredAssignments.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Agent</th>
                                            <th>Serviceable Area</th>
                                            <th>Sectors</th>
                                            <th>Assignment Type</th>
                                            <th>Status</th>
                                            <th>Assigned Date</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAssignments.map((assignment) => (
                                            <tr key={assignment._id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <UserCheck size={16} className="text-primary me-2" />
                                                        <div>
                                                            <div className="fw-bold">
                                                                {assignment.agent?.name || 'Unknown Agent'}
                                                            </div>
                                                            {assignment.agent?.phone && (
                                                                <small className="text-muted">
                                                                    {assignment.agent.phone}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Map size={16} className="text-success me-2" />
                                                        <div>
                                                            <div className="fw-semibold">
                                                                {assignment.serviceableArea?.name || 'Unknown Area'}
                                                            </div>
                                                            {assignment.serviceableArea && (
                                                                <small className="text-muted">
                                                                    {assignment.serviceableArea.city}, {assignment.serviceableArea.state}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {assignment.isFloating ? (
                                                        <span className="badge bg-info">
                                                            <Globe size={12} className="me-1" />
                                                            All Sectors
                                                        </span>
                                                    ) : (
                                                        <div>
                                                            {assignment.sectors && assignment.sectors.length > 0 ? (
                                                                <div>
                                                                    <span className="badge bg-secondary me-1">
                                                                        {assignment.sectors.length} sector{assignment.sectors.length > 1 ? 's' : ''}
                                                                    </span>
                                                                    <div className="mt-1">
                                                                        {assignment.sectors.slice(0, 2).map((sector, index) => (
                                                                            <small key={sector._id || index} className="text-muted d-block">
                                                                                {sector.name}
                                                                            </small>
                                                                        ))}
                                                                        {assignment.sectors.length > 2 && (
                                                                            <small className="text-muted">
                                                                                +{assignment.sectors.length - 2} more
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <em className="text-muted">No sectors</em>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getTypeColor(assignment.isFloating)}`}>
                                                        {assignment.isFloating ? (
                                                            <>
                                                                <Globe size={12} className="me-1" />
                                                                Floating
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Map size={12} className="me-1" />
                                                                Fixed
                                                            </>
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getStatusColor(assignment.isActive)}`}>
                                                        {getStatusText(assignment.isActive)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {new Date(assignment.createdAt).toLocaleDateString()}
                                                    </small>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Link
                                                            to={`/inventory/v1.2/agent-sector-assignments/${assignment._id}`}
                                                            className="btn btn-outline-info btn-sm"
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteClick(assignment)}
                                                            className="btn btn-outline-danger btn-sm"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <Users size={64} className="text-muted mb-3" />
                                <h5 className="text-muted">No agent assignments found</h5>
                                <p className="text-muted mb-4">
                                    {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || serviceableAreaFilter !== 'all'
                                        ? 'Try adjusting your search filters'
                                        : 'Create your first agent assignment to get started'
                                    }
                                </p>
                                {(!searchTerm && statusFilter === 'all' && typeFilter === 'all' && serviceableAreaFilter === 'all') && (
                                    <Link
                                        to="/inventory/v1.2/agent-sector-assignments/new"
                                        className="btn btn-primary"
                                    >
                                        <Plus size={16} className="me-2" />
                                        Create First Assignment
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title">
                                    <AlertTriangle size={20} className="text-danger me-2" />
                                    Confirm Deletion
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={cancelDelete}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Are you sure you want to delete the assignment for agent{' '}
                                    <strong>"{assignmentToDelete?.agent?.name}"</strong> in{' '}
                                    <strong>"{assignmentToDelete?.serviceableArea?.name}"</strong>?
                                </p>
                                <div className="alert alert-warning mt-3 mb-0">
                                    <small>
                                        <strong>Warning:</strong> This action cannot be undone and will remove 
                                        the agent's access to the assigned sectors and operations.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer border-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Assignment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

AgentSectorAssignments.propTypes = {
    getAgentAssignments: PropTypes.func.isRequired,
    deleteAgentAssignment: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getAgents: PropTypes.func.isRequired,
    agentAssignments: PropTypes.array,
    serviceableAreas: PropTypes.array,
    agents: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    agentAssignments: state.agentAssignment.agentAssignments,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    agents: state.agent.agents,
    loading: state.agentAssignment.loading
});

export default connect(mapStateToProps, {
    getAgentAssignments,
    deleteAgentAssignment,
    getServiceableAreas,
    getAgents
})(AgentSectorAssignments);
